import { useEffect, useMemo, useState } from 'react'

import {
	ChevronLeft,
	ChevronRight,
	GalleryVertical,
	Table,
	Tally3,
} from 'lucide-react'
import { useSelector } from 'react-redux'

import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { Button } from '@/shared/components/atoms/Button'
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/atoms/Tabs'
import { capitalizeFirstLetter } from '@/shared/utils/helpers'

import { goNext, goPrev, goToday, setView } from './CalendarUtils'
import { calendarRef } from '../../context/Data'

interface CalendarNavProps {
	calendarRef: calendarRef
	start: Date
	end: Date
	viewedDate: Date
}

export default function CalendarNav({
	calendarRef,
	viewedDate,
}: CalendarNavProps) {
	const { t } = useTranslation()
	const { language } = useSelector((state: RootState) => state.ui)

	// Determine initial view based on screen size (mobile = day, desktop = week)
	const initialView = useMemo(() => {
		if (typeof window !== 'undefined') {
			return window.innerWidth < 1024 ? 'timeGridDay' : 'timeGridWeek'
		}
		return 'timeGridWeek'
	}, [])

	const [currentView, setCurrentView] = useState(initialView)

	const locale = language?.startsWith('es') ? 'es-ES' : 'en-US'
	const selectedYear = viewedDate.getFullYear()
	const isMobile =
		typeof window !== 'undefined' &&
		window.matchMedia('(max-width: 1023px)').matches

	// Get localized month name
	const monthName = viewedDate.toLocaleDateString(locale, { month: 'long' })
	const fullDate = viewedDate.toLocaleDateString(locale, {
		month: 'long',
		day: 'numeric',
		year: 'numeric',
	})

	// Keep nav view state in sync with FullCalendar and lock mobile to daily view
	useEffect(() => {
		if (typeof window === 'undefined') return
		const calendarApi = calendarRef.current?.getApi()
		if (!calendarApi) return

		const mobile = window.matchMedia('(max-width: 1023px)').matches
		if (mobile && calendarApi.view.type !== 'timeGridDay') {
			calendarApi.changeView('timeGridDay')
			setCurrentView('timeGridDay')
			return
		}

		setCurrentView(calendarApi.view.type)
	}, [calendarRef, viewedDate])

	return (
		<div className="flex flex-wrap min-w-full justify-between items-center gap-3 h-8">
			{isMobile ? (
				<>
					<div className="flex items-center gap-1 h-full text-xs text-muted-foreground">
						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7"
							onClick={() => {
								goPrev(calendarRef)
							}}
						>
							<ChevronLeft className="h-4 w-4" />
						</Button>
						<span className="font-medium">
							{capitalizeFirstLetter(fullDate)}
						</span>
						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7"
							onClick={() => {
								goNext(calendarRef)
							}}
						>
							<ChevronRight className="h-4 w-4" />
						</Button>
					</div>
					<Button
						className="rounded-full h-7 px-3 text-[10px]"
						variant="outline"
						onClick={() => {
							goToday(calendarRef)
						}}
					>
						{t('entities.schedule.calendar.today')}
					</Button>
				</>
			) : (
				<>
					<div className="flex flex-row items-center gap-2 h-full">
						<Button
							variant="ghost"
							size="icon"
							className="h-full w-8"
							onClick={() => {
								goPrev(calendarRef)
							}}
						>
							<ChevronLeft className="h-4 w-4" />
						</Button>

						<span className="text-sm font-medium text-muted-foreground">
							{monthName} {selectedYear}
						</span>

						<Button
							variant="ghost"
							size="icon"
							className="h-full w-8"
							onClick={() => {
								goNext(calendarRef)
							}}
						>
							<ChevronRight className="h-4 w-4" />
						</Button>
					</div>

					<div className="flex flex-wrap gap-3 justify-between h-full items-center">
						<div className="flex flex-wrap gap-3 justify-center h-full items-center">
							<Button
								className="rounded-full md:rounded-lg h-full text-xs"
								variant="outline"
								onClick={() => {
									goToday(calendarRef)
								}}
							>
								{currentView === 'timeGridDay'
									? t('entities.schedule.calendar.today')
									: currentView === 'timeGridWeek'
										? t('entities.schedule.calendar.this_week')
										: currentView === 'dayGridMonth'
											? t('entities.schedule.calendar.this_month')
											: null}
							</Button>

							<Tabs defaultValue={initialView}>
								<TabsList className="hidden md:flex h-full gap-0 rounded-lg bg-muted p-[3px]">
									<TabsTrigger
										value="timeGridDay"
										onClick={() =>
											setView(calendarRef, 'timeGridDay', setCurrentView)
										}
										className="h-full min-h-0 min-w-0 rounded-md px-2 py-0"
									>
										<GalleryVertical className="h-4 w-4" />
									</TabsTrigger>
									<TabsTrigger
										value="timeGridWeek"
										onClick={() =>
											setView(calendarRef, 'timeGridWeek', setCurrentView)
										}
										className="h-full min-h-0 min-w-0 rounded-md px-2 py-0 hidden lg:block"
									>
										<Tally3 className="h-4 w-4" />
									</TabsTrigger>
									<TabsTrigger
										value="dayGridMonth"
										onClick={() =>
											setView(calendarRef, 'dayGridMonth', setCurrentView)
										}
										className="h-full min-h-0 min-w-0 rounded-md px-2 py-0 hidden lg:block"
									>
										<Table className="h-4 w-4 rotate-90" />
									</TabsTrigger>
								</TabsList>
							</Tabs>
						</div>
					</div>
				</>
			)}
		</div>
	)
}
